import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type ProfileResponse, type Subscriber } from '../api';
import { formatIsraeliTimestamp } from '../lib/format';

export function SubscriberProfilePage() {
  const navigate = useNavigate();
  const params = useParams<{ email: string }>();
  const email = params.email ? decodeURIComponent(params.email) : '';
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allSubscribers, setAllSubscribers] = useState<Subscriber[]>([]);
  const [notesSaveRequest, setNotesSaveRequest] = useState(0);
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesBusy, setNotesBusy] = useState(false);

  useEffect(() => {
    if (!email) return;
    setProfile(null);
    setError(null);
    api.getProfile(email)
      .then(setProfile)
      .catch((e: Error) => setError(e.message));
  }, [email]);

  useEffect(() => {
    let cancelled = false;
    api.listSubscribers()
      .then((r) => {
        if (!cancelled) setAllSubscribers(r.subscribers);
      })
      .catch(() => {
        if (!cancelled) setAllSubscribers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const profileEmail = profile?.subscriber.email ?? email;
  const currentIndex = useMemo(
    () => allSubscribers.findIndex((x) => x.email.toLowerCase() === profileEmail.toLowerCase()),
    [allSubscribers, profileEmail],
  );
  const prevSubscriber = currentIndex > 0 ? allSubscribers[currentIndex - 1] : null;
  const nextSubscriber =
    currentIndex >= 0 && currentIndex < allSubscribers.length - 1 ? allSubscribers[currentIndex + 1] : null;

  if (error) {
    return (
      <div className="card">
        <div className="error">Failed to load: {error}</div>
        <p style={{ marginTop: 16 }}><Link to="/">← Back to subscribers</Link></p>
      </div>
    );
  }
  if (!profile) return <div className="muted">Loading profile…</div>;

  const { subscriber: s, events, engagement: eng } = profile;
  const lastOpen = eng.lastOpenAt ? new Date(eng.lastOpenAt).toLocaleString() : '—';

  return (
    <>
      <div className="row profile-nav-row" style={{ marginBottom: 12, justifyContent: 'space-between' }}>
        <Link to="/">← All subscribers</Link>
        <div className="row profile-nav-controls" style={{ gap: 8 }}>
          <button
            type="button"
            className="primary profile-nav-btn"
            onClick={() => navigate('/compose', { state: { toEmail: s.email } })}
            disabled={s.status !== 'subscribed'}
            title={s.status !== 'subscribed' ? 'Subscriber is unsubscribed' : 'Compose an email to this subscriber'}
          >
            ✉ Send email
          </button>
          {currentIndex >= 0 && allSubscribers.length > 0 && (
            <span className="muted profile-nav-indicator" style={{ fontSize: 12 }}>
              Subscriber {currentIndex + 1} / {allSubscribers.length}
            </span>
          )}
          <button
            type="button"
            className="secondary profile-nav-btn"
            onClick={() => prevSubscriber && navigate(`/subscriber/${encodeURIComponent(prevSubscriber.email)}`)}
            disabled={!prevSubscriber}
          >
            ← Previous
          </button>
          <button
            type="button"
            className="secondary profile-nav-btn"
            onClick={() => nextSubscriber && navigate(`/subscriber/${encodeURIComponent(nextSubscriber.email)}`)}
            disabled={!nextSubscriber}
          >
            Next →
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: '0 0 4px' }}>{s.name || s.email}</h2>
            <div className="muted">{s.email}</div>
            <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
              <span className={`badge ${s.status}`}>{s.status}</span>
              {s.source && <> · source: <strong>{s.source}</strong></>}
              {s.date && <> · joined {formatIsraeliTimestamp(s.date)}</>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Engagement (last 90d)
            </div>
            <div style={{ marginTop: 6 }}>
              <span
                className={`badge ${eng.score === 'hot' ? 'subscribed' : eng.score === 'warm' ? '' : 'unsubscribed'}`}
                style={{ fontSize: 14 }}
              >
                {eng.score}
              </span>
            </div>
            <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
              {eng.opens} opens · {eng.clicks} clicks · last open {lastOpen}
            </div>
          </div>
        </div>
      </div>

      <TagsEditor
        email={s.email}
        initialTags={s.tags}
      />

      <FollowUpEditor email={s.email} />

      <NotesEditor
        email={s.email}
        initialNotes={s.notes}
        saveRequest={notesSaveRequest}
        onStateChange={({ dirty, busy }) => {
          setNotesDirty(dirty);
          setNotesBusy(busy);
        }}
      />

      <div className="card profile-quick-actions">
        <div className="row profile-quick-actions-row" style={{ justifyContent: 'space-between', gap: 8 }}>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="secondary profile-nav-btn"
              onClick={() => prevSubscriber && navigate(`/subscriber/${encodeURIComponent(prevSubscriber.email)}`)}
              disabled={!prevSubscriber}
            >
              ← Previous
            </button>
            <button
              type="button"
              className="secondary profile-nav-btn"
              onClick={() => nextSubscriber && navigate(`/subscriber/${encodeURIComponent(nextSubscriber.email)}`)}
              disabled={!nextSubscriber}
            >
              Next →
            </button>
          </div>
          <button
            type="button"
            className="primary profile-save-btn"
            onClick={() => setNotesSaveRequest((v) => v + 1)}
            disabled={!notesDirty || notesBusy}
          >
            {notesBusy ? 'Saving…' : 'Save notes'}
          </button>
        </div>
      </div>

      <div className="card">
        <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>
          Activity ({events.length} event{events.length === 1 ? '' : 's'})
        </h3>
        {events.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            No tracked activity yet. Tracking is off by default — enable it on a broadcast to start collecting data.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>Broadcast</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td>
                    <span className="badge">{e.type}</span>
                    {e.link_id != null && <span className="muted" style={{ marginLeft: 6 }}>link #{e.link_id}</span>}
                  </td>
                  <td>{e.broadcast_subject}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const TAG_RE = /^[a-z0-9][a-z0-9_-]*$/;

function FollowUpEditor({ email }: { email: string }) {
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const [savedAt, setSavedAt] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.listFollowUps()
      .then((r) => {
        if (cancelled) return;
        const current = r.followUps[email.toLowerCase()];
        const nextAt = toDatetimeLocal(current?.at ?? null);
        const nextNote = current?.note ?? '';
        setAt(nextAt);
        setNote(nextNote);
        setSavedAt(nextAt);
        setSavedNote(nextNote);
        setError(null);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  const dirty = at !== savedAt || note !== savedNote;
  const isDue = at ? new Date(at).getTime() <= Date.now() : false;

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.setFollowUp(email, fromDatetimeLocal(at), note);
      setSavedAt(at);
      setSavedNote(note);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    if (busy) return;
    setAt('');
    setNote('');
    setBusy(true);
    setError(null);
    try {
      await api.setFollowUp(email, null, '');
      setSavedAt('');
      setSavedNote('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>Follow-up reminder</h3>
        {at && (
          <span className={`badge ${isDue ? 'unsubscribed' : ''}`}>
            {isDue ? 'Due' : 'Scheduled'}
          </span>
        )}
      </div>
      <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label>When</label>
          <input
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
          />
        </div>
        <div style={{ flex: 2 }}>
          <label>Note</label>
          <input
            type="text"
            value={note}
            maxLength={160}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What to do on follow-up..."
          />
        </div>
      </div>
      <div className="row" style={{ marginTop: 8, gap: 6 }}>
        <button className="primary" onClick={save} disabled={!dirty || busy}>
          {busy ? 'Saving…' : 'Save reminder'}
        </button>
        <button className="secondary" onClick={clear} disabled={busy || (!at && !note)}>
          Clear
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function toDatetimeLocal(timestamp: number | null): string {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value: string): number | null {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function TagsEditor({ email, initialTags }: { email: string; initialTags: string[] }) {
  const [tags, setTags] = useState<string[]>(initialTags);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setTags(initialTags); }, [initialTags]);

  async function commit(next: string[]) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.setTags(email, next);
      setTags(r.tags);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function addTag() {
    const t = draft.trim().toLowerCase();
    if (!t) return;
    if (!TAG_RE.test(t)) {
      setError('Tags must start with a letter/digit and contain only letters, digits, hyphens, or underscores.');
      return;
    }
    if (tags.includes(t)) { setDraft(''); return; }
    setDraft('');
    commit([...tags, t]);
  }

  function removeTag(t: string) {
    commit(tags.filter((x) => x !== t));
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Tags</h3>
      <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {tags.length === 0 && <span className="muted" style={{ fontSize: 13 }}>No tags yet.</span>}
        {tags.map((t) => (
          <span
            key={t}
            className="badge"
            style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              disabled={busy}
              aria-label={`Remove tag ${t}`}
              style={{
                background: 'transparent', border: 0, color: 'inherit',
                cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1,
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="row" style={{ gap: 6 }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          placeholder="add a tag (e.g. beta, vip)"
          disabled={busy}
          style={{ maxWidth: 320 }}
        />
        <button className="secondary" onClick={addTag} disabled={busy || !draft.trim()}>
          {busy ? '…' : 'Add'}
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function NotesEditor({
  email,
  initialNotes,
  saveRequest,
  onStateChange,
}: {
  email: string;
  initialNotes: string;
  saveRequest: number;
  onStateChange: (state: { dirty: boolean; busy: boolean }) => void;
}) {
  const [value, setValue] = useState(initialNotes);
  const [saved, setSaved] = useState(initialNotes);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setValue(initialNotes); setSaved(initialNotes); }, [initialNotes]);

  const dirty = value !== saved;

  useEffect(() => {
    onStateChange({ dirty, busy });
  }, [dirty, busy, onStateChange]);

  async function save() {
    if (busy || !dirty) return;
    setBusy(true);
    setError(null);
    try {
      await api.setNotes(email, value);
      setSaved(value);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (saveRequest > 0) void save();
    // save() intentionally omitted from deps; it depends on current state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequest]);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>Notes</h3>
        {dirty && <span className="muted" style={{ fontSize: 12 }}>unsaved changes</span>}
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={4}
        placeholder="Anything to remember about this subscriber…"
        disabled={busy}
        style={{ width: '100%', resize: 'vertical' }}
      />
      <div className="row" style={{ marginTop: 8, gap: 6 }}>
        <button className="primary" onClick={save} disabled={!dirty || busy}>
          {busy ? 'Saving…' : 'Save notes'}
        </button>
        {dirty && (
          <button className="secondary" onClick={() => setValue(saved)} disabled={busy}>
            Discard
          </button>
        )}
      </div>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}
